import React, { useCallback, useEffect, useState } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination, Badge, Card } from 'react-bootstrap';
import { ScrollText, Download, Search, RotateCcw } from 'lucide-react';
import useAuthHeaders from '../hooks/useAuthHeaders';
import './Bitacora.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 50;

// Coincide con la conservación de la bitácora (180 días) y con el tope que valida el backend.
const MAX_DIAS_RANGO = 180;

const toDateInput = (d) => d.toISOString().slice(0, 10);

const hoy = new Date();
const defaultDesde = toDateInput(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 29));
const defaultHasta = toDateInput(hoy);

const RESULTADO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'Ok', label: 'Exitosas' },
  { value: 'Error', label: 'Con error' },
];

// Las fechas llegan del API ya calculadas en las dos zonas. Se muestra la de México, que es la que el
// contador reconoce, y el UTC queda en el título y en el export.
const formatFecha = (dto) => {
  if (!dto?.ocurridoEnMexico) return '—';
  const d = new Date(dto.ocurridoEnMexico);
  return d.toLocaleString('es-MX', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const formatDuracion = (ms) => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

const ResultadoBadge = ({ resultado, razonError }) => {
  const esError = resultado === 'Error';
  return (
    <Badge bg={esError ? 'danger' : 'success'} title={esError ? razonError || undefined : undefined}>
      {esError ? 'Error' : 'Ok'}
    </Badge>
  );
};

const Bitacora = () => {
  const { getAuthHeaders } = useAuthHeaders();

  const [desdeInput, setDesdeInput] = useState(defaultDesde);
  const [hastaInput, setHastaInput] = useState(defaultHasta);
  const [rfcInput, setRfcInput] = useState('');
  const [operacionInput, setOperacionInput] = useState('');
  const [resultadoInput, setResultadoInput] = useState('');

  const [query, setQuery] = useState({
    desde: defaultDesde,
    hasta: defaultHasta,
    rfc: '',
    operacion: '',
    resultado: '',
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(null);

  const buildParams = useCallback((q) => {
    const params = new URLSearchParams();
    // Se mandan como fechas sin hora a propósito: el backend las interpreta como días naturales de
    // México (00:00 a 23:59:59 de ese día allá) y las convierte a UTC. Mandar un instante con zona
    // aquí desplazaría el rango 6 horas respecto a lo que el usuario eligió en pantalla.
    if (q.desde) params.set('desde', q.desde);
    if (q.hasta) params.set('hasta', q.hasta);
    if (q.rfc) params.set('rfc', q.rfc);
    if (q.operacion) params.set('operacion', q.operacion);
    if (q.resultado) params.set('resultado', q.resultado);
    return params;
  }, []);

  const fetchBitacora = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = buildParams(q);
      params.set('page', q.page);
      params.set('pageSize', q.pageSize);

      const response = await fetch(`${API_BASE}/bitacora?${params}`, { headers });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Error ${response.status}`);
      }
      setResult(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, buildParams]);

  useEffect(() => {
    fetchBitacora(query);
  }, [query, fetchBitacora]);

  const rangoEnDias = () => {
    const d = new Date(desdeInput);
    const h = new Date(hastaInput);
    return Math.round((h - d) / (1000 * 60 * 60 * 24));
  };

  const rangoInvalido = () => {
    const dias = rangoEnDias();
    return Number.isNaN(dias) || dias < 0 || dias > MAX_DIAS_RANGO;
  };

  const handleBuscar = () => {
    setQuery((prev) => ({
      ...prev,
      desde: desdeInput,
      hasta: hastaInput,
      rfc: rfcInput.trim().toUpperCase(),
      operacion: operacionInput.trim(),
      resultado: resultadoInput,
      page: 1,
    }));
  };

  const handleLimpiar = () => {
    setDesdeInput(defaultDesde);
    setHastaInput(defaultHasta);
    setRfcInput('');
    setOperacionInput('');
    setResultadoInput('');
    setQuery({
      desde: defaultDesde,
      hasta: defaultHasta,
      rfc: '',
      operacion: '',
      resultado: '',
      page: 1,
      pageSize: PAGE_SIZE,
    });
  };

  const handlePage = (p) => setQuery((prev) => ({ ...prev, page: p }));

  const handleExportar = async (formato) => {
    setExportando(formato);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = buildParams(query);
      params.set('formato', formato);

      const response = await fetch(`${API_BASE}/bitacora/export?${params}`, { headers });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || `Error ${response.status}`);
      }

      // El archivo se arma en el navegador a partir del blob para poder mandar el header de
      // Authorization, que un <a href> no llevaría.
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bitacora_${query.desde}_${query.hasta}.${formato}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportando(null);
    }
  };

  const renderPaginacion = () => {
    if (result.totalPages <= 1) return null;

    const actual = result.page;
    const total = result.totalPages;
    const desde = Math.max(1, actual - 2);
    const hasta = Math.min(total, desde + 4);
    const paginas = [];
    for (let p = desde; p <= hasta; p += 1) paginas.push(p);

    return (
      <Pagination className="mb-0">
        <Pagination.First onClick={() => handlePage(1)} disabled={actual === 1} />
        <Pagination.Prev onClick={() => handlePage(actual - 1)} disabled={actual === 1} />
        {paginas.map((p) => (
          <Pagination.Item key={p} active={p === actual} onClick={() => handlePage(p)}>
            {p}
          </Pagination.Item>
        ))}
        <Pagination.Next onClick={() => handlePage(actual + 1)} disabled={actual === total} />
        <Pagination.Last onClick={() => handlePage(total)} disabled={actual === total} />
      </Pagination>
    );
  };

  return (
    <div className="bitacora-container">
      <div className="bitacora-header">
        <div>
          <h2 className="bitacora-title">
            <ScrollText size={24} className="me-2" />
            Bitácora de operaciones
          </h2>
          <p className="bitacora-subtitle">
            Registro de las operaciones realizadas desde tu cuenta contra los portales de las autoridades.
            Se conserva por 6 meses y puedes exportarlo en cualquier momento.
          </p>
        </div>
      </div>

      <Card className="bitacora-filtros mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Label className="bitacora-label">Desde</Form.Label>
              <Form.Control
                type="date"
                value={desdeInput}
                onChange={(e) => setDesdeInput(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="bitacora-label">Hasta</Form.Label>
              <Form.Control
                type="date"
                value={hastaInput}
                onChange={(e) => setHastaInput(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="bitacora-label">RFC</Form.Label>
              <Form.Control
                type="text"
                placeholder="Todos"
                value={rfcInput}
                onChange={(e) => setRfcInput(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="bitacora-label">Operación</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej. CsfFiel"
                value={operacionInput}
                onChange={(e) => setOperacionInput(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="bitacora-label">Resultado</Form.Label>
              <Form.Select value={resultadoInput} onChange={(e) => setResultadoInput(e.target.value)}>
                {RESULTADO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} className="d-flex gap-2">
              <Button variant="primary" onClick={handleBuscar} disabled={rangoInvalido()}>
                <Search size={16} className="me-1" />
                Buscar
              </Button>
              <Button variant="outline-secondary" onClick={handleLimpiar} title="Limpiar filtros">
                <RotateCcw size={16} />
              </Button>
            </Col>
          </Row>
          {rangoInvalido() && (
            <div className="bitacora-rango-error mt-2">
              El rango debe ir de menor a mayor y no puede exceder {MAX_DIAS_RANGO} días,
              que es el plazo de conservación de la bitácora.
            </div>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <div className="bitacora-toolbar">
        <span className="bitacora-total">
          {loading ? 'Cargando…' : `${result.totalCount.toLocaleString('es-MX')} operaciones`}
        </span>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleExportar('csv')}
            disabled={!!exportando || result.totalCount === 0}
          >
            {exportando === 'csv'
              ? <Spinner animation="border" size="sm" className="me-1" />
              : <Download size={16} className="me-1" />}
            Exportar CSV
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleExportar('json')}
            disabled={!!exportando || result.totalCount === 0}
          >
            {exportando === 'json'
              ? <Spinner animation="border" size="sm" className="me-1" />
              : <Download size={16} className="me-1" />}
            Exportar JSON
          </Button>
        </div>
      </div>

      <Card className="bitacora-tabla-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="bitacora-loading">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : result.items.length === 0 ? (
            <div className="bitacora-vacio">
              No hay operaciones registradas en el periodo seleccionado.
            </div>
          ) : (
            <Table responsive hover className="bitacora-tabla mb-0">
              <thead>
                <tr>
                  <th>Fecha (hora de México)</th>
                  <th>Operador</th>
                  <th>RFC</th>
                  <th>Operación</th>
                  <th>Resultado</th>
                  <th>Duración</th>
                  <th>Canal</th>
                  <th>IP</th>
                  <th>Acuse</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((item) => (
                  <tr key={item.id}>
                    <td title={`UTC: ${item.ocurridoEnUtc}`}>{formatFecha(item)}</td>
                    <td className="bitacora-operador">{item.operadorEmail || '—'}</td>
                    <td>{item.rfc || '—'}</td>
                    <td className="bitacora-operacion">{item.operation}</td>
                    <td>
                      <ResultadoBadge resultado={item.resultado} razonError={item.razonError} />
                    </td>
                    <td>{formatDuracion(item.durationMs)}</td>
                    <td>{item.canal || '—'}</td>
                    <td className="bitacora-ip">{item.ipAddress || '—'}</td>
                    <td>{item.acuseRef || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <div className="bitacora-paginacion">{renderPaginacion()}</div>
    </div>
  );
};

export default Bitacora;
